import { Flex } from 'antd';
import styles from './documents.module.scss';
import { DocumentsTable } from '@/components/documents-table/documents-table';
import { DocumentsHeader } from '@/components/documents-header/documents-header';
import { Outlet, useOutlet } from 'react-router';
import { useDocuments } from '@/hooks/use-documents/use-documents';
import { useEffect, useState } from 'react';
import { DocumentsFilters } from '@/services/documents-service/documents-service.types';
import {
  DEFAULT_PAGINATION,
  MAX_PAGE_SIZE,
  useSortAndPagination,
} from '@/hooks/use-sort-and-pagination/use-sort-and-pagination';
import { OrderBy } from '@/hooks/use-sort-and-pagination/use-sort-and-pagination.types';
import { useUser } from '@/hooks/use-user/use-user';
import { useCompanyStore } from '@/store/app.store';
import { useCompanySearch } from '@/hooks/use-company-search/use-company-search';
import { useCompaniesList } from '@/hooks/use-company/use-company';
import { readUrlCount, readUrlString } from '@/utils/url-state/url-state';
import { useUrlSync } from '@/hooks/use-url-sync/use-url-sync';
import { useCompanyAccessGuard } from '@/hooks/use-company-access-guard/use-company-access-guard';
import { useInitialSearchParams } from '@/hooks/use-initial-search-params/use-initial-search-params';

export function Documents() {
  const outlet = useOutlet();
  const { companyName, setCompanyName } = useCompanyStore();
  const { userData } = useUser();
  const isCompanyAdmin = userData?.roles.find((role) => role.name === 'company_admin');
  const { companies } = useCompaniesList();
  const isAdmin = isCompanyAdmin && !!userData?.default_company;

  const initialUrl = useInitialSearchParams();

  const [documentsFilters, setDocumentsFilters] = useState<DocumentsFilters>({
    query: readUrlString(initialUrl, 'query'),
    type: readUrlString(initialUrl, 'type'),
    company_name:
      readUrlString(initialUrl, 'company_name') ??
      companyName ??
      (isCompanyAdmin ? userData?.default_company : userData?.company?.name),
  });

  useEffect(() => {
    const urlCompany = readUrlString(initialUrl, 'company_name');
    if (urlCompany && urlCompany !== companyName) {
      setCompanyName(urlCompany);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (readUrlString(initialUrl, 'company_name')) return;
    if (companyName) {
      setDocumentsFilters((prev) => ({
        ...prev,
        company_name: companyName,
      }));
    } else if (isCompanyAdmin && userData?.default_company) {
      setDocumentsFilters((prev) => ({
        ...prev,
        company_name: userData.default_company,
      }));
    }
  }, [companyName, isCompanyAdmin, userData?.default_company]);

  const { paginationDetails, sortDetails, onPaginationChanged, onSortChange } =
    useSortAndPagination({
      defaultPagination: {
        page: readUrlCount(initialUrl, 'page') ?? DEFAULT_PAGINATION.page,
        items_per_page:
          readUrlCount(initialUrl, 'items_per_page', MAX_PAGE_SIZE) ??
          DEFAULT_PAGINATION.items_per_page,
      },
      defaultSorting: {
        sort_by: readUrlString(initialUrl, 'sort_by') ?? 'created_at',
        order_by: (readUrlString(initialUrl, 'order_by') ?? 'desc') as OrderBy,
      },
    });

  useCompanyAccessGuard(documentsFilters.company_name, companies, (allowedName) => {
    setDocumentsFilters((prev) => ({ ...prev, company_name: allowedName }));
    setCompanyName(allowedName);
  });

  useUrlSync(
    {
      query: documentsFilters.query,
      type: documentsFilters.type,
      company_name: documentsFilters.company_name,
      page: paginationDetails.page,
      items_per_page: paginationDetails.items_per_page,
      sort_by: sortDetails?.sort_by,
      order_by: sortDetails?.order_by,
    },
    {
      defaults: {
        page: DEFAULT_PAGINATION.page,
        items_per_page: DEFAULT_PAGINATION.items_per_page,
        sort_by: 'created_at',
        order_by: 'desc',
      },
    }
  );

  const { documentsData, isPending: isPendingDocumentsData } = useDocuments({
    filtersParams: {
      ...documentsFilters,
      company_name: documentsFilters.company_name,
    },
    paginationParams: { ...paginationDetails, ...sortDetails },
  });

  const handleChange = (page?: number, perPage?: number, sortBy?: string, orderBy?: OrderBy) => {
    const nextSortBy = sortBy || 'created_at';
    const nextOrderBy = orderBy || 'desc';
    const sortChanged =
      nextSortBy !== sortDetails?.sort_by || nextOrderBy !== sortDetails?.order_by;

    onPaginationChanged(
      sortChanged ? DEFAULT_PAGINATION.page : page || DEFAULT_PAGINATION.page,
      perPage || DEFAULT_PAGINATION.items_per_page
    );
    onSortChange(nextSortBy, nextOrderBy);
  };

  // A filter change invalidates the current offset: page 4 of the old result set is usually empty
  // in the new one.
  const handleCompanyChange = (filters: DocumentsFilters) => {
    setDocumentsFilters(filters);
    setCompanyName(filters.company_name ?? undefined);
    onPaginationChanged(DEFAULT_PAGINATION.page, paginationDetails.items_per_page);
  };

  const [companySearchQuery, setCompanySearchQuery] = useState('');
  const { companySearchData, companySearchLoading, companySearchError } = useCompanySearch({
    query: companySearchQuery,
  });

  return (
    <Flex vertical className={styles.container}>
      {outlet ? (
        <Outlet context={{ companiesList: companies?.map((company) => company.name) || [] }} />
      ) : (
        <div>
          <DocumentsHeader
            onSearchQueryChange={setCompanySearchQuery}
            searchResults={companySearchData}
            isSearchLoading={companySearchLoading}
            searchError={companySearchError}
            filters={documentsFilters}
            onChangeFilters={handleCompanyChange}
            documentsNumber={documentsData?.documents_count || 0}
            companiesList={companies?.map((company) => company.name) || []}
          />
          <DocumentsTable
            currentPage={paginationDetails.page}
            pageSize={paginationDetails.items_per_page}
            onChange={handleChange}
            isAdmin={isAdmin}
            pagination={documentsData?.pagination}
            documentsData={documentsData?.documents}
            loading={isPendingDocumentsData}
            sortBy={sortDetails?.sort_by}
            orderBy={sortDetails?.order_by}
          />
        </div>
      )}
    </Flex>
  );
}
